import { Component, computed, inject, input, InputSignal, signal, Signal, TemplateRef } from '@angular/core';

import { _SourceList_Base_ToolComponent } from './_source-list-base-tool.component';
import { IListButtonData } from 'src/app/features/shared/lists/models/_base-list-button';
import { ILevelGeneralListDto, levelGeneralListDto } from '../../models/level-general-list-dto';
import { ILevelGeneralListData } from '../../models/level-general-list-data';
import { _SourceList_Base_FeatureComponent } from './_source-list-base-feature.component';
import { TRequestName } from 'src/assets/common/enums/request-names.g';
import { catchError, EMPTY, map, Observable, switchMap, tap } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { tabMode, TTabMode } from '../../models/tab-mode';
import { inactiveMode } from '../../models/inactive-mode';
import { DialogService } from 'src/app/features/shared/dialogs/dialog.service';

@Component({
    selector: 'app-tablist-base-feature-component',
    template: '',
})
export abstract class _NavigationList_Base_FeatureComponent<ListDto extends ILevelGeneralListDto, ListData extends ILevelGeneralListData>
    extends _SourceList_Base_FeatureComponent<ListDto, ListData>
{
    abstract list$$: Signal<ListData[] | null>;
    abstract btnAddData$$: Signal<IListButtonData | null>;
    abstract btnDeleteData$$: Signal<IListButtonData | null>;
    abstract buttonsHide$$: Signal<boolean | null>;
    abstract noClick$$: Signal<boolean | null>;

    abstract input_btnAddData$$: InputSignal<IListButtonData | null>;
    abstract input_btnDeleteData$$: InputSignal<IListButtonData | null>;
    abstract input_buttonsHide$$: InputSignal<boolean | null>;
    abstract input_noClick$$: InputSignal<boolean | null>;

    protected abstract _restore_patch$(restoreList: any[], httpParams: HttpParams | null): Observable<ListDto>;

    abstract emit_btnAddClick(item: ILevelGeneralListDto): void;
    abstract emit_onClick(item: ListData): void;
    abstract emit_onRestore(restoreList: any[]): void;
    abstract ngOnInit(): void;

    private readonly __dialogService = inject(DialogService);

    protected _input_btnAddData$$ = input<IListButtonData | null>(null, {alias: 'btnAddData'});
    protected _input_btnDeleteData$$ = input<IListButtonData | null>(null, {alias: 'btnDeleteData'});
    protected _input_buttonsHide$$ = input<boolean | null>(null, {alias: 'buttonsHide'});
    protected _input_noClick$$ = input<boolean | null>(null, {alias: 'noClick'});

    protected _computed_list$$ = computed(() => this._listService.listAll$$());

    protected _tabMode$$ = signal<TTabMode>(tabMode.mainList);
    tabMode$$ = this._tabMode$$;
    // Voor [inactiveMode] op de lijst-tool in de feature-templates (zie inactive-mode.ts).
    protected readonly _inactiveMode = inactiveMode;

    protected _ngOnInit(): void {
        super._ngOnInit();
    }

    emit_onDeleteDeep(deleteList: any[]): void {
        this._deleteDeep$(deleteList).subscribe();
    }

    private __confirm$(title: string, confirmLabel: string): Observable<boolean> {
        return this.__dialogService.showChoice<boolean>(
            [
                { label: confirmLabel, value: true },
                { label: 'Annuleren', value: false },
            ],
            title,
        ).pipe(map(__value => __value === true));
    }

    protected _deleteDeep$ = (deleteList: any[]): Observable<ListDto> => {
        const __httpParams = this.httpParams$$();

        return this.__confirm$('Geselecteerde items definitief verwijderen?', 'Verwijderen').pipe(
            switchMap(__confirmed => {
                if (!__confirmed) return EMPTY;

                return this._deleteDeep_delete$(deleteList, __httpParams).pipe(
                    tap(_ => {
                        this._listService.list_remove(deleteList);
                        this._listService.list_refresh$();
                        this._tabListService.tabList_setRefresh(true);
                        this._tabMode$$.set(tabMode.mainList);
                    })
                );
            }),
            // HTTP-fout: de middleware heeft al genotificeerd; side-effects niet draaien.
            catchError(() => EMPTY),
        );
    }

    protected _deleteDeep_delete$(deleteList: any[], httpParams: HttpParams | null): Observable<ListDto> {
        return EMPTY;
    }

    protected _restore$ = (restoreList: any[], request_name?: TRequestName, requestOptions?: any): Observable<ListDto> => {
        const __httpParams = this.httpParams$$();

        return this.__confirm$('Geselecteerde items terugzetten?', 'Terugzetten').pipe(
            switchMap(__confirmed => {
                if (!__confirmed) return EMPTY;

                return this._restore_patch$(restoreList, __httpParams).pipe(
                    tap(_ => {
                        const __list = this.list$$()?.filter(item => restoreList.findIndex(r => r.guid === item.guid) >= 0) ?? [];

                        __list.forEach(item => item[levelGeneralListDto.deleted] = item[levelGeneralListDto.inactive] = false);
                        this._listService.list_refresh$();
                        this._tabListService.tabList_setRefresh(true);
                        this._tabMode$$.set(tabMode.mainList);
                    })
                );
            }),
            // HTTP-fout: de middleware heeft al genotificeerd; side-effects niet draaien.
            catchError(() => EMPTY),
        );
    }
}
